import { action, computed, makeObservable, observable } from 'mobx'
import type { FirebaseRepository } from '../models/FirebaseRepository'
import { Cubit } from './core'
import type { StaticModelCollection } from '../types'
import { GradeSyncJob } from '../models/GradeSyncJob'
import {
  createGradeSyncJob,
  deleteGradeSyncJob,
  getActiveGradeSyncJobs,
} from '../firestore/GradeSyncJob'

export class GradeSyncJobsCubit extends Cubit {
  repository: FirebaseRepository
  jobs: StaticModelCollection<GradeSyncJob>

  // Track jobs we've already shown toasts for to avoid duplicates
  @observable
  private processedJobIds = new Set<string>()

  constructor({ repository }: { repository: FirebaseRepository }) {
    super()
    makeObservable(this)
    this.repository = repository
    this.jobs = GradeSyncJob.emptyCollection(repository)
  }

  dispose() {
    this.closeAllStreams()
    return super.dispose()
  }

  /**
   * Start listening to active grade sync jobs for the current user
   */
  startJobsStream(): void {
    if (this.hasStream('gradeSyncJobs')) return

    const userId = this.repository.uid
    if (!userId) return

    this.addStream(
      getActiveGradeSyncJobs(this.repository, userId),
      (jobs) => {
        this.jobs.replaceModels(jobs)
      },
      {
        name: 'gradeSyncJobs',
      }
    )
  }

  /**
   * Get jobs that have completed but haven't been processed yet (for showing toasts)
   */
  @computed
  get completedJobsToProcess(): GradeSyncJob[] {
    return this.jobs.models.filter(
      (job) => job.isCompleted && !this.processedJobIds.has(job.id)
    )
  }

  /**
   * Get jobs that have failed but haven't been processed yet (for showing toasts)
   */
  @computed
  get failedJobsToProcess(): GradeSyncJob[] {
    return this.jobs.models.filter(
      (job) => job.isFailed && !this.processedJobIds.has(job.id)
    )
  }

  /**
   * Mark a job as processed (toast shown) and delete it from Firestore
   */
  @action
  markJobAsProcessed(jobId: string): void {
    this.processedJobIds.add(jobId)

    const userId = this.repository.uid
    if (userId) {
      // Clean up the job document from Firestore
      deleteGradeSyncJob(this.repository, userId, jobId).catch((error) => {
        // Log but don't throw - job might already be deleted or user may have navigated away
        // eslint-disable-next-line no-console
        console.debug('Failed to delete grade sync job:', jobId, error)
      })
    }
  }

  /**
   * Create a new grade sync job
   */
  async createJob(params: {
    sectionId: string
    assignmentId: string
    roomStateId?: string
    targetUserId?: string
    forceResend: boolean
  }): Promise<string> {
    return createGradeSyncJob(this.repository, params)
  }
}
